import React, { useMemo } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { GoogleMap, useJsApiLoader, CircleF, OverlayViewF, OverlayView } from "@react-google-maps/api";
import { MapOutlined, HomeOutlined } from "@mui/icons-material";
import { dt } from "../../../shared/themes/redesignTokens";
import { GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_SCRIPT_ID } from "../../../shared/config/googleMapsConfig";
import useTranslation from "../../../shared/hooks/useTranslation";
import { useAppSelector } from "../../../shared/hooks/sharedHooks";
import { ServiceBoardState } from "../models/models";

// ===================================================================
//  TYPES
// ===================================================================

interface ServiceBoardMiniMapProps {
  lat?: number | null;
  lng?: number | null;
  radiusKm?: number;
  onOpenMap: () => void;
}

// ===================================================================
//  MAP STYLES (minimal, clean — matches RfsMiniMap)
// ===================================================================

const mapStyles: google.maps.MapTypeStyle[] = [
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "poi.business", stylers: [{ visibility: "off" }] },
  { featureType: "transit", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
];

// ===================================================================
//  RADIUS CIRCLE OVERLAY STYLE (matches MapContainer)
// ===================================================================

const RADIUS_CIRCLE_OPTIONS: google.maps.CircleOptions = {
  strokeColor: "#3498DB",
  strokeOpacity: 0.8,
  strokeWeight: 2,
  fillColor: "#3498DB",
  fillOpacity: 0.06,
};

// ===================================================================
//  HOME MARKER (simplified inline version for mini-map)
// ===================================================================

const HOME_MARKER_SIZE = 28;

const MiniHomeMarker: React.FC<{ position: { lat: number; lng: number } }> = ({ position }) => {
  const pos = useMemo(() => ({ lat: position.lat, lng: position.lng }), [position.lat, position.lng]);
  const getOffset = useMemo(
    () => () => ({ x: -(HOME_MARKER_SIZE / 2), y: -(HOME_MARKER_SIZE / 2) }),
    []
  );

  return (
    <OverlayViewF
      position={pos}
      mapPaneName={OverlayView.OVERLAY_LAYER}
      getPixelPositionOffset={getOffset}
    >
      <div
        style={{
          width: HOME_MARKER_SIZE,
          height: HOME_MARKER_SIZE,
          borderRadius: "50%",
          backgroundColor: "#3498DB",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
          border: "2px solid #FFFFFF",
          pointerEvents: "none",
        }}
      >
        <HomeOutlined style={{ color: "#FFFFFF", fontSize: "16px" }} />
      </div>
    </OverlayViewF>
  );
};

// ===================================================================
//  ZOOM CALCULATION — auto-zoom based on radius
// ===================================================================

const computeZoom = (radiusKm: number): number => {
  if (radiusKm <= 0) return 10;
  if (radiusKm <= 10) return 12;
  if (radiusKm <= 25) return 11;
  if (radiusKm <= 50) return 10;
  if (radiusKm <= 100) return 9;
  if (radiusKm <= 200) return 8;
  if (radiusKm <= 300) return 7;
  if (radiusKm <= 450) return 6;
  return 5;
};

// ===================================================================
//  COMPONENT
// ===================================================================

const ServiceBoardMiniMap = ({ lat: latProp, lng: lngProp, radiusKm: radiusKmProp, onOpenMap }: ServiceBoardMiniMapProps) => {
  const { translate } = useTranslation(["serviceBoard"]);
  const isPhone = useMediaQuery("(max-width:768px)");
  const isTablet = useMediaQuery("(max-width:1200px)");

  // Read provider settings from Redux when props aren't provided
  const { providerSettings } = useAppSelector<ServiceBoardState>((state: any) => state.customerServiceBoard);
  const lat = latProp ?? providerSettings?.effectiveLatitude;
  const lng = lngProp ?? providerSettings?.effectiveLongitude;
  const radiusKm = radiusKmProp ?? providerSettings?.serviceRadiusKm ?? 0;

  const apiKey = (window as any).app?.config?.googleMapsApiKey || "";
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // Hide on phone
  if (isPhone) return null;

  const hasLocation = lat != null && lng != null && !isNaN(lat) && !isNaN(lng);

  const mapHeight = isTablet ? 140 : 160;

  // No location data — show placeholder
  if (!hasLocation) {
    return null;
  }

  const center = { lat: lat!, lng: lng! };
  const zoom = computeZoom(radiusKm);

  // Loading state
  if (!isLoaded) {
    return (
      <Box
        sx={{
          height: mapHeight,
          width: "100%",
          borderRadius: "12px",
          border: `1px solid ${dt.cardBorder}`,
          backgroundColor: dt.inputBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: "10px",
        }}
      >
        <Box sx={{ fontSize: "12px", fontFamily: dt.font, color: dt.textMuted }}>
          {translate("serviceBoard:mapLoading")}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      onClick={onOpenMap}
      sx={{
        position: "relative",
        height: mapHeight,
        width: "100%",
        borderRadius: "12px",
        border: `1px solid ${dt.cardBorder}`,
        overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        cursor: "pointer",
        mb: "10px",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          boxShadow: "0 4px 12px rgba(0,0,0,0.10)",
          borderColor: dt.blue,
          transform: "scale(1.01)",
        },
      }}
    >
      {/* Google Map — non-interactive */}
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={center}
        zoom={zoom}
        options={{
          styles: mapStyles,
          disableDefaultUI: true,
          gestureHandling: "none",
          draggable: false,
          clickableIcons: false,
          zoomControl: false,
          scrollwheel: false,
          disableDoubleClickZoom: true,
          keyboardShortcuts: false,
        }}
      >
        {/* Service radius circle */}
        {radiusKm > 0 && (
          <CircleF
            center={center}
            radius={radiusKm * 1000}
            options={RADIUS_CIRCLE_OPTIONS}
          />
        )}

        {/* Home marker */}
        <MiniHomeMarker position={center} />
      </GoogleMap>

      {/* "Show on map" overlay button */}
      <Box
        sx={{
          position: "absolute",
          bottom: "10px",
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          alignItems: "center",
          gap: "5px",
          background: `linear-gradient(135deg, ${dt.blue} 0%, #2E86C1 100%)`,
          color: "#FFFFFF",
          fontFamily: dt.font,
          fontSize: "12px",
          fontWeight: 600,
          borderRadius: "24px",
          padding: "6px 16px",
          boxShadow: "0 2px 8px rgba(52,152,219,0.35)",
          pointerEvents: "none",
          whiteSpace: "nowrap",
        }}
      >
        <MapOutlined sx={{ fontSize: "14px" }} />
        {translate("serviceBoard:miniMapShowOnMap")}
      </Box>
    </Box>
  );
};

export default React.memo(ServiceBoardMiniMap);
